import React, { useEffect, useRef, useState } from 'react';

import ModalDialog from '../../app/Shared/ModalDialog';
import { getCurrentSite } from '../../common/utils';
import DetailsAPI from '../DetailsAPI';

interface Props {
  stockItemId: number;
  children?: (openModal: () => void) => React.ReactElement;
}

const ReportErrorModal = ({ stockItemId, children }: Props) => {
  /** Transition duration for modal entrance and exit */
  const transitionDuration = 150;

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const [isProblemDirty, setIsProblemDirty] = useState(false);
  const [description, setDescription] = useState('');
  const [isDescriptionDirty, setIsDescriptionDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const problemSelectRef = useRef();

  const validErrorOptions = [
    {
      label: 'Preview does not play',
      value: 'Preview does not play',
      relevantSites: ['videoblocks'],
    },
    {
      label: 'File does not download',
      value: 'File does not download',
    },
    {
      label: 'File does not work after downloading',
      value: 'File does not work after downloading',
    },
    {
      label: 'File not the same as shown in preview',
      value: 'File not the same as shown in preview',
    },
    {
      label: 'Report AI-Generated content',
      value: 'Report AI-Generated content',
    },
    {
      label: 'Other',
      value: 'Other',
    },
  ];

  const currentErrorOptions = validErrorOptions.filter(
    (option) =>
      !option.relevantSites || option.relevantSites.includes(getCurrentSite())
  );
  const currentErrorOptionsValues = currentErrorOptions.map(
    (option) => option.value
  );
  const isProblemValid =
    !isProblemDirty || (problem && currentErrorOptionsValues.includes(problem));
  const isDescriptionValid = !isDescriptionDirty || !!description;
  const allowSubmission =
    isProblemValid && isDescriptionValid && !!problem && !!description;

  useEffect(() => {
    if (!isProblemValid && !isDescriptionValid) {
      setErrorMessage('Please select a problem and enter a brief description');
    } else if (isProblemValid && !isDescriptionValid) {
      setErrorMessage('Please enter a brief description of the problem');
    } else if (!isProblemValid && isDescriptionValid) {
      setErrorMessage('Please select a problem');
    }
  }, [isProblemValid, isDescriptionValid]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    /** Make sure the modal has exited before resetting state */
    setTimeout(() => {
      setIsOpen(false);
      setIsSaving(false);
      setProblem('');
      setIsProblemDirty(false);
      setDescription('');
      setIsDescriptionDirty(false);
      setErrorMessage('');
      setSuccessMessage('');
    }, transitionDuration);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (allowSubmission) {
      setIsSaving(true);
      DetailsAPI.saveErrorReport(stockItemId, problem, description)
        .then((response) => response.json())
        .then((json) => {
          if (!json.success) {
            throw new Error(json.message);
          }

          setErrorMessage('');
          setSuccessMessage(json.message);
        })
        .catch((e) => {
          setErrorMessage(e.payload.message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleInputChange = (inputSetter) => {
    inputSetter();
    setErrorMessage('');
  };

  const handleProblemSelectBlur = () => {
    if (problem === '' && !isProblemDirty) {
      return;
    }

    setIsProblemDirty(true);
  };

  const handleDescriptionInputBlur = () => {
    if (description === '' && !isDescriptionDirty && !isProblemDirty) {
      return;
    } else if (!!description && !isProblemDirty) {
      setIsProblemDirty(true);
    }

    setIsDescriptionDirty(true);
  };

  return (
    <>
      {children ? (
        children(() => setIsOpen(true))
      ) : (
        <div className="flex justify-end h-5">
          <button
            className="hover:underline m-0 text-xs text-blue-700 bg-transparent border-none"
            id="report-error-button"
            onClick={() => setIsOpen(true)}
          >
            Report an Error
          </button>
        </div>
      )}

      <ModalDialog
        id="report-error-modal"
        isOpen={isOpen}
        initiallyFocusedElement={problemSelectRef}
        contentLabel="Report Error"
        onRequestClose={handleClose}
        shouldCloseOnOverlayClick={true}
        closeTimeoutMS={transitionDuration}
      >
        {!!successMessage ? (
          <div
            className="flex flex-col items-center space-y-10"
            aria-live="assertive"
          >
            <p className="success-message m-0">{successMessage}</p>
            <button
              className="hover:bg-yellow-600 px-8 py-1 text-base font-semibold text-center transition-colors bg-yellow-500 border-none rounded-full cursor-pointer"
              id="close-button"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-8">
              <div className="w-full">
                <label
                  htmlFor="problem-select"
                  className={`block w-full m-0 font-semibold ${
                    isProblemValid ? '' : 'text-red-500'
                  }`}
                >
                  Problem
                </label>
                <select
                  className={`w-full px-3 py-2 border border-solid rounded ${
                    isProblemValid ? 'border-gray-400' : 'border-red-500'
                  }`}
                  id="problem-select"
                  onBlur={handleProblemSelectBlur}
                  onChange={({ target }) =>
                    handleInputChange(() => setProblem(target.value))
                  }
                  ref={problemSelectRef}
                  value={problem}
                >
                  <option disabled defaultValue value="">
                    What problem are you having?
                  </option>
                  {currentErrorOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full">
                <label
                  htmlFor="description-input"
                  className={`block w-full m-0 font-semibold ${
                    isDescriptionValid ? '' : 'text-red-500'
                  }`}
                >
                  Description
                </label>
                <textarea
                  className={`leading w-full px-3 py-2 text-sm leading-5 tracking-wide border border-solid rounded ${
                    isDescriptionValid ? 'border-gray-400' : 'border-red-500'
                  }`}
                  id="description-input"
                  name="description-input"
                  rows={4}
                  onBlur={handleDescriptionInputBlur}
                  onChange={({ target }) =>
                    handleInputChange(() => setDescription(target.value))
                  }
                  value={description}
                />
              </div>
            </div>

            <p
              className="px-2 m-0 mt-2 space-x-2 text-red-500"
              aria-live="polite"
            >
              {errorMessage?.length > 0 && (
                <>
                  <i
                    className="fa fa-exclamation-circle"
                    aria-hidden="true"
                  ></i>
                  <span>{errorMessage}</span>
                </>
              )}
            </p>

            <div className="flex-nowrap flex items-center justify-end w-full mt-10 space-x-10">
              <button
                className="hover:underline disabled:cursor-not-allowed px-3 py-2 text-base text-center text-blue-500 bg-transparent border-none"
                id="cancel-button"
                disabled={isSaving}
                onClick={handleClose}
              >
                Cancel
              </button>

              <button
                className="disabled:cursor-not-allowed disabled:opacity-75 hover:bg-yellow-600 w-32 px-8 py-1 text-base font-semibold text-center transition-colors bg-yellow-500 border-none rounded-full cursor-pointer"
                disabled={!allowSubmission || isSaving}
                id="submit-button"
                type="submit"
              >
                {isSaving ? (
                  <i
                    className="fa fa-circle-o-notch fa-spin"
                    aria-hidden="true"
                  ></i>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        )}
      </ModalDialog>
    </>
  );
};

export default ReportErrorModal;
